import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Command } from '@fabricos/protocol-ts';
import { diagnoseBehaviour, lintParams } from '@/components/assets/paramLint';
import { cancelGoto } from '@/lib/cancelGoto';
import { sendGoto } from '@/lib/gotoSend';
import { formatFlightMode } from '@/lib/flightMode';
import { sendCommand } from '@/hooks/useHubSocket';
import { recordEvent } from './appEvents';
import { useFleet } from './fleet';
import { useGotoTargets } from './gotoTargets';
import { useParams } from './params';
import { useSelection } from './selection';

/**
 * Diva — the operator copilot. Rule-based intent parser over the live fleet:
 * status questions are answered directly from telemetry; anything that moves
 * a vehicle becomes a ProposedAction chip that only executes on Confirm,
 * through the exact same command path as the Pilot UI (sendCommand /
 * sendGoto / cancelGoto). Every confirm/reject lands in the audit panel and
 * the app event register.
 */
export type MessageRole = 'user' | 'assistant';
export type ActionKind = 'command' | 'parameter-change' | 'mission-edit' | 'preflight' | 'noop';
export type ActionStatus = 'pending' | 'confirmed' | 'rejected';

export interface ProposedAction {
  id: string;
  kind: ActionKind;
  title: string;
  /** Short human-readable summary, used in audit log + on the action chip. */
  summary: string;
  /** Optional structured payload for display. */
  changes: { key: string; from: string | number; to: string | number }[];
  status: ActionStatus;
  /** Robot this action targets (display + execution). */
  robot_id?: string;
  robot_name?: string;
  /** The exact protocol command Confirm dispatches (except cancel_goto). */
  command?: Command;
  /** Non-Command executions. */
  special?: 'cancel_goto';
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  body: string;
  ts: number;
  action?: ProposedAction;
}

export interface AuditEntry {
  id: string;
  ts: number;
  actor: string;
  kind: ActionKind;
  summary: string;
  result: 'confirmed' | 'rejected';
}

interface AssistantStore {
  messages: ChatMessage[];
  audit: AuditEntry[];
  draft: string;
  busy: boolean;

  sendUser: (body: string) => void;
  confirm: (messageId: string) => void;
  reject: (messageId: string) => void;
  setDraft: (s: string) => void;
  resetConversation: () => void;
}

function makeId(): string {
  return `m-${Math.random().toString(36).slice(2, 10)}`;
}
function makeCmdId(): string {
  return `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

const GREETING: ChatMessage = {
  id: 'm-greeting',
  role: 'assistant',
  ts: 0,
  body:
    "I'm Diva — I fly the fleet with you and help set it up. Select a drone (or name it), then:\n\n" +
    '- **arm** / **disarm** / **take off to 15 m** / **land** / **rtl** / **hold**\n' +
    '- **go to 28.5450, 77.1926** — GOTO by coordinates · **set speed 8** · **cancel goto**\n' +
    '- **status** — position, speeds, battery, GPS\n' +
    '- **check my params** — rule-based config lint (failsafes, cals, sanity ranges)\n' +
    '- describe a problem — *"it circles in loiter"*, *"won\'t arm"*, *"wobbles"* — and I\'ll name the suspect params\n' +
    '- **calibration help** — what to run and in which order\n\n' +
    'Anything that moves a vehicle shows a confirm chip first — nothing flies on my own.',
};

export const useAssistant = create<AssistantStore>()(
  persist(
    (set, get) => ({
      messages: [GREETING],
      audit: [],
      draft: '',
      busy: false,

      setDraft: (draft) => set({ draft }),

      sendUser: (body) => {
        if (!body.trim()) return;
        recordEvent('diva', `prompt: ${body.trim().slice(0, 60)}`);
        const userMsg: ChatMessage = { id: makeId(), role: 'user', body, ts: Date.now() };
        set((s) => ({ messages: [...s.messages, userMsg], draft: '', busy: true }));
        // Tiny delay so the reply visibly follows the prompt.
        setTimeout(() => {
          const reply = interpret(body);
          set((s) => ({ messages: [...s.messages, reply], busy: false }));
        }, 250);
      },

      confirm: (messageId) => {
        const s = get();
        const m = s.messages.find((x) => x.id === messageId);
        if (!m || !m.action || m.action.status !== 'pending') return;
        const action: ProposedAction = { ...m.action, status: 'confirmed' };

        // Execute through the same paths the Pilot UI uses.
        let outcome = `Confirmed — ${action.summary}.`;
        try {
          if (action.special === 'cancel_goto' && action.robot_id) {
            cancelGoto(action.robot_id);
          } else if (action.command) {
            if (action.command.kind === 'goto') {
              // sendGoto owns marker/queue/history; chat confirm replaces the
              // far-target prompt.
              sendGoto(action.command.robot_id, action.command.lat, action.command.lon, {
                skipConfirm: true,
              });
            } else {
              sendCommand(action.command);
            }
          }
          recordEvent('diva', action.summary.toLowerCase(), {
            robotId: action.robot_id,
            robotName: action.robot_name,
          });
        } catch {
          outcome = `Tried to execute but the send failed — check the Hub connection.`;
        }

        set((st) => ({
          messages: [
            ...st.messages.map((x) => (x.id === messageId ? { ...x, action } : x)),
            { id: makeId(), role: 'assistant' as const, body: outcome, ts: Date.now() },
          ],
          audit: [
            {
              id: makeId(),
              ts: Date.now(),
              actor: 'operator',
              kind: action.kind,
              summary: action.summary,
              result: 'confirmed' as const,
            },
            ...st.audit,
          ].slice(0, 50),
        }));
      },

      reject: (messageId) =>
        set((s) => {
          const m = s.messages.find((x) => x.id === messageId);
          if (!m || !m.action || m.action.status !== 'pending') return s;
          const action: ProposedAction = { ...m.action, status: 'rejected' };
          recordEvent('diva', `rejected: ${action.summary.toLowerCase()}`, {
            robotId: action.robot_id,
            robotName: action.robot_name,
          });
          return {
            messages: s.messages.map((x) => (x.id === messageId ? { ...x, action } : x)),
            audit: [
              {
                id: makeId(),
                ts: Date.now(),
                actor: 'operator',
                kind: action.kind,
                summary: action.summary,
                result: 'rejected' as const,
              },
              ...s.audit,
            ].slice(0, 50),
          };
        }),

      resetConversation: () => set({ messages: [GREETING] }),
    }),
    // v2: v1 persisted demo transcripts + fake audit.
    { name: 'fabricos.assistant.v2' },
  ),
);

/* =================================================================== */
/* Intent parsing                                                       */
/* =================================================================== */

interface RobotCtx {
  id: string;
  name: string;
}

/** Robot resolution: named in the prompt → selected → the only one flying. */
function resolveRobot(lower: string): RobotCtx | { error: string } {
  const robots = useFleet.getState().robots;
  const entries = Object.values(robots);
  for (const r of entries) {
    if (lower.includes(r.name.toLowerCase()) || lower.includes(r.id.toLowerCase())) {
      return { id: r.id, name: r.name };
    }
  }
  const selected = useSelection.getState().selected;
  if (selected.size >= 1) {
    const id = Array.from(selected)[0] as string;
    return { id, name: robots[id]?.name ?? id };
  }
  if (entries.length === 1) return { id: entries[0]!.id, name: entries[0]!.name };
  if (entries.length === 0) return { error: 'No drones are connected — spawn one on the SITL page first.' };
  return { error: 'Which drone? Select one in the sidebar or name it (e.g. "auto-1, take off").' };
}

function propose(
  robot: RobotCtx,
  title: string,
  summary: string,
  command: Command | undefined,
  extras: Partial<ProposedAction> = {},
): ProposedAction {
  return {
    id: makeId(),
    kind: 'command',
    title,
    summary,
    changes: [],
    status: 'pending',
    robot_id: robot.id,
    robot_name: robot.name,
    command,
    ...extras,
  };
}

function interpret(prompt: string): ChatMessage {
  const lower = prompt.toLowerCase();
  const reply = (body: string, action?: ProposedAction): ChatMessage => ({
    id: makeId(),
    role: 'assistant',
    body,
    ts: Date.now(),
    action,
  });

  // ---- Info intents first (no robot command involved). ----
  if (/\b(help|what can you do)\b/.test(lower)) {
    return reply(GREETING.body);
  }

  if (/calibrat(e|ion)/.test(lower) && /help|how|guide|order|what/.test(lower)) {
    return reply(
      '**Calibration order for a new airframe** (all in Assets → Calibrate, vehicle DISARMED):\n\n' +
        '1. **Accelerometer** — six-position wizard; hold each pose rock-steady.\n' +
        '2. **Compass** — start, then rotate the vehicle through every axis until each bar fills. Away from metal and speakers. Reboot after.\n' +
        '3. **Gyro** — sit-still, seconds. Re-run any time drift appears.\n' +
        '4. **Level horizon** — vehicle in its level flight attitude.\n\n' +
        'The wizard shows the vehicle orientation art per step and streams live progress. Prompts and verdicts also land in Assets → Logs.',
    );
  }

  // Everything below needs a robot.
  const robot = resolveRobot(lower);
  if ('error' in robot) return reply(robot.error);
  const state = useFleet.getState().states[robot.id];
  const armed = state?.arm === 'armed';
  const inFlight = (state?.position?.alt_rel_home_m ?? 0) > 0.5;
  const base = { id: makeCmdId(), robot_id: robot.id, priority: 'normal' as const };

  if (/check.*(param|config)|param.*(check|ok|issue|problem)|config check/.test(lower)) {
    const table = useParams.getState().byRobot[robot.id] ?? {};
    if (Object.keys(table).length === 0) {
      return reply(
        `I can't lint what isn't loaded — open **Assets → Params** and click **Load all params** for ${robot.name}, then ask me again.`,
      );
    }
    const findings = lintParams(table);
    if (findings.length === 0) {
      return reply(
        `**${robot.name}** config check: clean. ${Object.keys(table).length} params loaded, no rule-based findings — failsafes, calibration flags and sanity ranges all pass.`,
      );
    }
    const lines = findings
      .slice(0, 8)
      .map(
        (f) =>
          `- ${f.severity === 'critical' ? '🔴' : f.severity === 'warning' ? '🟠' : '🔵'} **${f.title}** — ${f.fix} (\`${f.params[0]}\`)`,
      )
      .join('\n');
    return reply(
      `**${robot.name}** config check — ${findings.length} finding${findings.length === 1 ? '' : 's'}:\n\n${lines}\n\nFull detail in **Assets → Params → Config check**.`,
    );
  }

  {
    const diag = diagnoseBehaviour(prompt);
    if (diag) {
      return reply(
        `That sounds like **${diag.matched}**.\n\n${diag.explanation}\n\n` +
          `Suspect params: ${diag.suspects.map((s) => `\`${s}\``).join(' · ')} — inspect them in **Assets → Params**, and run **check my params** for the full lint.`,
      );
    }
  }

  if (/\b(status|where|how is|battery|report)\b/.test(lower)) {
    const pos = state?.position;
    const batt = state?.battery;
    return reply(
      `**${robot.name}** — ${armed ? 'ARMED' : 'disarmed'} · mode **${formatFlightMode(state?.mode)}**\n\n` +
        `- Position: ${pos ? `${pos.lat.toFixed(5)}, ${pos.lon.toFixed(5)} at ${(pos.alt_rel_home_m ?? 0).toFixed(1)} m AGL` : 'no fix yet'}\n` +
        `- Ground speed: ${(state?.velocity?.ground_speed_ms ?? 0).toFixed(1)} m/s · vertical ${(state?.velocity?.vz_ms ?? 0).toFixed(1)} m/s\n` +
        `- Battery: ${batt?.voltage_v?.toFixed(1) ?? '—'} V${batt?.remaining_pct != null ? ` (${Math.round(batt.remaining_pct)}%)` : ''}\n` +
        `- GPS: ${state?.gps?.fix ?? '—'} · ${state?.gps?.satellites ?? '—'} sats · link ${state?.connection ?? '—'}`,
    );
  }

  // ---- Command intents (confirm-gated). ----
  if (/\bdisarm\b/.test(lower)) {
    if (!armed) return reply(`${robot.name} is already disarmed.`);
    return reply(
      `Disarm **${robot.name}**? Motors stop immediately.`,
      propose(robot, `Disarm ${robot.name}`, `Disarm ${robot.name}`, { kind: 'disarm', ...base }),
    );
  }
  if (/\barm\b/.test(lower)) {
    if (armed) return reply(`${robot.name} is already armed.`);
    return reply(
      `Arm **${robot.name}**? Props will spin.`,
      propose(robot, `Arm ${robot.name}`, `Arm ${robot.name}`, { kind: 'arm', ...base }),
    );
  }
  if (/take ?off/.test(lower)) {
    const altMatch = lower.match(/take ?off (?:to |at )?(\d+(?:\.\d+)?)/);
    const alt = Math.min(120, Math.max(2, altMatch ? Number(altMatch[1]) : 10));
    if (!armed) {
      return reply(
        `${robot.name} is disarmed — arm first, then ask me to take off. Want me to arm it?`,
        propose(robot, `Arm ${robot.name}`, `Arm ${robot.name}`, { kind: 'arm', ...base }),
      );
    }
    return reply(
      `Take **${robot.name}** off to **${alt} m**?`,
      propose(robot, `Takeoff to ${alt} m`, `Takeoff ${robot.name} to ${alt} m`, {
        kind: 'takeoff',
        ...base,
        alt_m: alt,
      }),
    );
  }
  if (/\bland\b/.test(lower)) {
    if (!inFlight) return reply(`${robot.name} is already on the ground.`);
    return reply(
      `Land **${robot.name}** at its current position?`,
      propose(robot, `Land ${robot.name}`, `Land ${robot.name}`, { kind: 'land', ...base }),
    );
  }
  if (/\b(rtl|return|go home|come home)\b/.test(lower)) {
    if (!inFlight) return reply(`${robot.name} is on the ground — nothing to return.`);
    return reply(
      `Send **${robot.name}** home (RTL)?`,
      propose(robot, `RTL ${robot.name}`, `RTL ${robot.name}`, { kind: 'rtl', ...base }),
    );
  }
  if (/\b(hold|loiter|stay|pause)\b/.test(lower)) {
    return reply(
      `Hold **${robot.name}** in place (LOITER)?`,
      propose(robot, `Hold ${robot.name}`, `Hold ${robot.name} (LOITER)`, {
        kind: 'set_mode',
        ...base,
        mode: 'loiter',
      }),
    );
  }
  if (/cancel.*goto|abort.*goto|stop.*goto/.test(lower)) {
    const hasTarget = useGotoTargets.getState().targets[robot.id] != null;
    if (!hasTarget) return reply(`${robot.name} has no active GOTO.`);
    return reply(
      `Cancel **${robot.name}**'s GOTO and hold position?`,
      propose(robot, `Cancel GOTO`, `Cancel ${robot.name}'s GOTO`, undefined, {
        special: 'cancel_goto',
      }),
    );
  }
  {
    const coords = prompt.match(/(-?\d{1,2}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);
    if (coords && /go ?to|goto|fly/.test(lower)) {
      const lat = Number(coords[1]);
      const lon = Number(coords[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
        const alt = useGotoTargets.getState().settings.defaultAltAglM;
        return reply(
          `Fly **${robot.name}** to **${lat.toFixed(5)}, ${lon.toFixed(5)}** at ${alt} m AGL?`,
          propose(
            robot,
            `GOTO ${lat.toFixed(4)}, ${lon.toFixed(4)}`,
            `GOTO ${robot.name} → ${lat.toFixed(5)}, ${lon.toFixed(5)} @ ${alt} m`,
            {
              kind: 'goto',
              ...base,
              lat,
              lon,
              alt_m: alt,
              speed_ms: null,
              yaw_rad: null,
            },
          ),
        );
      }
    }
  }
  {
    const speed = lower.match(/speed (?:to |at )?(\d+(?:\.\d+)?)/);
    if (speed) {
      const v = Math.min(20, Math.max(0.5, Number(speed[1])));
      const type = /climb/.test(lower) ? 'climb' : /descen[dt]/.test(lower) ? 'descent' : 'ground';
      return reply(
        `Set **${robot.name}**'s ${type} speed to **${v} m/s**? Applies now, lasts until the next mode change.`,
        propose(robot, `${type} speed → ${v} m/s`, `Set ${robot.name} ${type} speed to ${v} m/s`, {
          kind: 'change_speed',
          ...base,
          speed_type: type,
          speed_ms: v,
        }),
      );
    }
  }

  return reply(
    `I didn't catch a command in that. ${GREETING.body.split('\n\n')[1] ?? ''}\n\nOr ask for **status**.`,
  );
}

export const KIND_LABEL: Record<ActionKind, string> = {
  command: 'Robot command',
  'parameter-change': 'Parameter change',
  'mission-edit': 'Mission edit',
  preflight: 'Pre-flight',
  noop: 'Note',
};
